import React from 'react';
import { TextField } from '../../components/forms/TextField';
import { OperatingHours, type WeekHours } from '../../components/forms/OperatingHours';
import { RichTextEditor } from '../../components/forms/RichTextEditor';
import { useSettings } from './useSettings';
import { useI18n } from '../../i18n/I18nContext';
import {
  SectionDivider, Toggle, TextArea, Select,
  Toast, PageHeader, PageFooter, LoadingSpinner,
} from './SettingWidgets';

export function SettingPage() {
  const { t } = useI18n();
  const {
    form, set, loading, saving, toast,
    handleSave, handleDiscard,
  } = useSettings();

  const SCHEDULE_TYPES = [
    { key: 'offsite', label: t('settings.scheduleTypes.offsite') },
    { key: 'onsite', label: t('settings.scheduleTypes.onsite') },
  ];

  const LANGUAGE_OPTIONS = [
    { value: '', label: t('settings.fields.defaultLanguageAuto') },
    { value: 'en', label: t('settings.fields.defaultLanguageEn') },
    { value: 'id', label: t('settings.fields.defaultLanguageId') },
  ];


  const handleScheduleChange = (typeKey: string, week: WeekHours) => {
    if (typeKey === 'offsite') set('offsiteHours', week);
    else set('onsiteHours', week);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="mx-auto max-w-4xl space-y-0">
      <Toast toast={toast} />

      <PageHeader
        title={t('settings.title')}
        description={t('settings.subtitle')}
        saving={saving}
        onSave={handleSave}
      />

      {/* General */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="border-b border-slate-100 px-4 py-4 sm:px-6 dark:border-slate-800">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{t('settings.sections.general.title')}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('settings.sections.general.subtitle')}</p>
        </div>
        <div className="space-y-5 px-4 py-5 sm:px-6">
          <Toggle
            label={t('settings.fields.allowCustomerLogin')}
            description={t('settings.fields.allowCustomerLoginHint')}
            checked={form.allowCustomerLogin}
            onChange={(v) => set('allowCustomerLogin', v)}
          />
          <Toggle
            label={t('settings.fields.productSectionEnabled')}
            description={t('settings.fields.productSectionEnabledHint')}
            checked={form.productSectionEnabled}
            onChange={(v) => set('productSectionEnabled', v)}
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Select
              label={t('settings.fields.defaultLanguage')}
              description={t('settings.fields.defaultLanguageHint')}
              value={form.defaultLanguage}
              onChange={(v) => set('defaultLanguage', v)}
              options={LANGUAGE_OPTIONS}
            />
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <TextField
              label={t('settings.fields.motto')}
              placeholder={t('settings.fields.mottoPlaceholder')}
              value={form.motto}
              onChange={(e) => set('motto', (e.target as HTMLInputElement).value)}
            />
            <TextField
              label={t('settings.fields.welcomeMessage')}
              placeholder={t('settings.fields.welcomeMessagePlaceholder')}
              value={form.welcomeMessage}
              onChange={(e) => set('welcomeMessage', (e.target as HTMLInputElement).value)}
            />
          </div>
        </div>
      </div>

      <SectionDivider title={t('settings.dividers.information')} />

      {/* Contact Information */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="border-b border-slate-100 px-4 py-4 sm:px-6 dark:border-slate-800">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{t('settings.sections.contactInformation.title')}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('settings.sections.contactInformation.subtitle')}</p>
        </div>
        <div className="space-y-5 px-4 py-5 sm:px-6">
          <TextArea
            label={t('settings.fields.address')}
            placeholder={t('settings.fields.addressPlaceholder')}
            value={form.address}
            onChange={(v) => set('address', v)}
          />
          <TextField
            label={t('settings.fields.addressLink')}
            placeholder={t('settings.fields.addressLinkPlaceholder')}
            value={form.addressLink}
            onChange={(e) => set('addressLink', (e.target as HTMLInputElement).value)}
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <TextField
              label={t('settings.fields.contactNo')}
              placeholder={t('settings.fields.contactNoPlaceholder')}
              value={form.contactNo}
              onChange={(e) => set('contactNo', (e.target as HTMLInputElement).value)}
            />
            <TextField
              label={t('settings.fields.contactEmail')}
              placeholder={t('settings.fields.contactEmailPlaceholder')}
              type="email"
              value={form.contactEmail}
              onChange={(e) => set('contactEmail', (e.target as HTMLInputElement).value)}
            />
          </div>
        </div>
      </div>

      <SectionDivider title={t('settings.dividers.booking')} />

      {/* Booking Configuration */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="border-b border-slate-100 px-4 py-4 sm:px-6 dark:border-slate-800">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{t('settings.sections.bookingConfiguration.title')}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('settings.sections.bookingConfiguration.subtitle')}</p>
        </div>
        <div className="space-y-5 px-4 py-5 sm:px-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Toggle
              label={t('settings.fields.allowOffsiteBooking')}
              description={t('settings.fields.allowOffsiteBookingHint')}
              checked={form.allowOffsiteBooking}
              onChange={(v) => set('allowOffsiteBooking', v)}
            />
            <Toggle
              label={t('settings.fields.allowOnsiteBooking')}
              description={t('settings.fields.allowOnsiteBookingHint')}
              checked={form.allowExternalBooking}
              onChange={(v) => set('allowExternalBooking', v)}
            />
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <TextField
              label={t('settings.fields.offsiteLabel')}
              placeholder={t('settings.fields.offsiteLabelPlaceholder')}
              value={form.offsiteName}
              onChange={(e) => set('offsiteName', (e.target as HTMLInputElement).value)}
            />
            <TextField
              label={t('settings.fields.onsiteLabel')}
              placeholder={t('settings.fields.onsiteLabelPlaceholder')}
              value={form.externalName}
              onChange={(e) => set('externalName', (e.target as HTMLInputElement).value)}
            />
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <TextField
              label={t('settings.fields.remarksLabel')}
              placeholder={t('settings.fields.remarksLabelPlaceholder')}
              value={form.remarkLabel}
              onChange={(e) => set('remarkLabel', (e.target as HTMLInputElement).value)}
            />
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <TextField
              label={t('settings.fields.offsiteInterval')}
              placeholder={t('settings.fields.offsiteIntervalPlaceholder')}
              value={form.offsiteInterval}
              onChange={(e) => set('offsiteInterval', (e.target as HTMLInputElement).value)}
            />
            <TextField
              label={t('settings.fields.onsiteInterval')}
              placeholder={t('settings.fields.onsiteIntervalPlaceholder')}
              value={form.onsiteInterval}
              onChange={(e) => set('onsiteInterval', (e.target as HTMLInputElement).value)}
            />
          </div>
        </div>
      </div>

      <SectionDivider title={t('settings.dividers.operatingHours')} />

      {/* Operating Hours */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="border-b border-slate-100 px-4 py-4 sm:px-6 dark:border-slate-800">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{t('settings.sections.operatingHours.title')}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('settings.sections.operatingHours.subtitle')}</p>
        </div>
        <div className="px-4 py-5 sm:px-6">
          <OperatingHours
            types={SCHEDULE_TYPES}
            hours={{ offsite: form.offsiteHours, onsite: form.onsiteHours }}
            onChange={handleScheduleChange}
          />
        </div>
      </div>

      <SectionDivider title={t('settings.dividers.others')} />

      {/* Additional Settings */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="border-b border-slate-100 px-4 py-4 sm:px-6 dark:border-slate-800">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{t('settings.sections.additionalSettings.title')}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('settings.sections.additionalSettings.subtitle')}</p>
        </div>
        <div className="space-y-5 px-4 py-5 sm:px-6">
          <div className="max-w-xs">
            <TextField
              label={t('settings.fields.commissionPercentage')}
              placeholder={t('settings.fields.commissionPercentagePlaceholder')}
              type="number"
              value={form.commissionPercentage}
              onChange={(e) => set('commissionPercentage', (e.target as HTMLInputElement).value)}
            />
          </div>
          <RichTextEditor
            label={t('settings.fields.invoiceNotes')}
            placeholder={t('settings.fields.invoiceNotesPlaceholder')}
            value={form.invoiceNotes}
            onChange={(v) => set('invoiceNotes', v)}
          />
        </div>
      </div>

      <PageFooter saving={saving} onSave={handleSave} onDiscard={handleDiscard} />
    </div>
  );
}
